import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { getActingOrganizationId } from '../utils/actingOrganization';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

export interface StoreApprovalInfoRequest {
  message: string;
  requestedAt: string;
  requestedBy: string;
}

export interface StoreApprovalState {
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  notes: string | null;
  infoRequest: StoreApprovalInfoRequest | null;
}

export interface StoreApprovalStatusResult {
  organizationId: string | null;
  organizationStatus: string | null;
  approval: StoreApprovalState | null;
  /** The store is in explore mode: catalog reads only until an admin approves it. */
  isPendingReview: boolean;
  /** An admin rejected the application; the server locks the organization out. */
  isRejected: boolean;
  isLoading: boolean;
}

/**
 * Statuses that mean "awaiting admin review". Must match the server's explore-mode
 * gate (`isPendingStoreStatus` / pendingOrganizationGuard: pending_verification only).
 * If PR #566 widens the server side, widen this list in the same change.
 */
export const AWAITING_REVIEW_STATUSES = ['pending_verification'];
export const isAwaitingReviewStatus = (status: string | null | undefined): boolean =>
  !!status && AWAITING_REVIEW_STATUSES.includes(status);

/** How often a store under review re-reads its status (paused while the tab is hidden). */
export const APPROVAL_POLL_INTERVAL_MS = 30 * 1000;

/**
 * Review state of the signed-in store's application.
 * Vendors have their own flow (useVendorStatus); admins impersonating a store
 * see that store's state so they can preview explore mode.
 */
export const useStoreApprovalStatus = (): StoreApprovalStatusResult => {
  const { currentUser, userProfile, isAdmin } = useAuth();
  const { getEffectiveUserType, impersonatedOrg } = useImpersonation();
  const effectiveUserType = getEffectiveUserType(userProfile);
  const isStore = effectiveUserType === 'store';
  const impersonatedOrgId = impersonatedOrg?.id || null;
  // The organization the person switched to (sent as X-Organization-Id on this
  // call); part of the key so a switch reads the new organization's status.
  const actingOrgId = getActingOrganizationId(currentUser?.uid);

  const { data, isLoading } = useQuery({
    queryKey: ['store-approval-status', impersonatedOrgId || 'self', actingOrgId || 'primary'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/onboarding/approval-status', { headers });
      // Throw so a transient failure keeps the last good data (and the poll
      // interval) instead of caching null and switching the refresh off.
      if (!res.ok) throw new Error(`approval-status ${res.status}`);
      const body = await res.json();
      return (body?.data || null) as {
        organizationId: string | null;
        organizationStatus: string | null;
        approval: StoreApprovalState | null;
        pendingReview: boolean;
      } | null;
    },
    enabled: isStore && !!userProfile && !(isAdmin && !impersonatedOrgId),
    retry: false,
    staleTime: 30 * 1000,
    // Keep asking while the application is under review so an approval (or
    // rejection) shows up without a reload. react-query pauses interval
    // refetches in a hidden tab and refetches on focus, so no polling happens
    // in the background. Nothing polls once the store has left review.
    refetchInterval: (query) =>
      isAwaitingReviewStatus(query.state.data?.organizationStatus) ? APPROVAL_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  // While impersonating, the impersonated org's status is authoritative and
  // already in hand; /onboarding/approval-status describes the admin's own profile.
  const impersonatedStatus = impersonatedOrgId ? (impersonatedOrg as { status?: string } | null)?.status || null : null;
  const organizationStatus = impersonatedOrgId ? impersonatedStatus : data?.organizationStatus || null;

  return {
    organizationId: impersonatedOrgId || data?.organizationId || null,
    organizationStatus,
    approval: impersonatedOrgId ? null : data?.approval || null,
    isPendingReview: isStore && isAwaitingReviewStatus(organizationStatus),
    isRejected: isStore && (organizationStatus === 'rejected' || organizationStatus === 'denied'),
    isLoading: impersonatedOrgId ? false : isLoading,
  };
};
